import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import { DateTimePicker, PickersInputComponentLocaleText, renderDigitalClockTimeView } from '@mui/x-date-pickers';
import OutsideAlerter from 'shared/components/outside-alerter/outside-alerter';

import { FilterChipDateTimeRangeProps, useFilterChipDateTimeRange } from './hooks/index';
import styles from './filter-chip-date-time-range.module.scss';

const DateTimeRangeFilterChip = ({
  placeHolder,
  initialDateFilters,
  onDateFiltersChanged,
  minimumStartDate,
  timeRange,
}: Readonly<FilterChipDateTimeRangeProps>): React.JSX.Element => {
  const { t } = useTranslation('filterChip');

  const dateTimePickerLocaleText: PickersInputComponentLocaleText = {
    fieldYearPlaceholder: () => t('dateTimePickerLocale.fieldYearPlaceholder'),
    fieldDayPlaceholder: () => t('dateTimePickerLocale.fieldDayPlaceholder'),
    fieldHoursPlaceholder: () => t('dateTimePickerLocale.fieldHoursPlaceholder'),
    fieldMinutesPlaceholder: () => t('dateTimePickerLocale.fieldMinutesPlaceholder'),
    fieldWeekDayPlaceholder: () => t('dateTimePickerLocale.fieldWeekDayPlaceholder'),
    toolbarTitle: t('dateTimePickerLocale.toolbarTitle'),
    cancelButtonLabel: t('dateTimePickerLocale.cancelButton'),
    clearButtonLabel: t('dateTimePickerLocale.clearButton'),
    okButtonLabel: t('dateTimePickerLocale.okButton'),
  };

  const customToolbarStyle = {
    '& .MuiTypography-root': {
      fontSize: 16,
    },
    '& .MuiButtonBase-root': {
      background: 'white',
    },
    '& .MuiButtonBase-root:active': {
      background: 'white',
    },
    '& .MuiButtonBase-root:hover': {
      background: 'white',
    },
    '& .MuiTouchRipple-root': {
      display: 'none',
    },
  };

  const selectedDayStyle = {
    sx: {
      '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected.Mui-focusVisible': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
      },
    },
  };

  const {
    filterChipIsOpen,
    handleOnChipClicked,
    isAnyDateFilterFilled,
    dateFilters,
    handleDateFromChange,
    openDatePicker,
    closeDatePicker,
    updateDateTo,
    maxDateForTheUntilFilter,
    handleCancel,
    handleApply,
    handleOutsideAlerter,
  } = useFilterChipDateTimeRange({
    placeHolder,
    initialDateFilters,
    onDateFiltersChanged,
    minimumStartDate,
    timeRange,
  });

  const [hasError, setHasError] = useState<boolean>(false);

  return (
    <OutsideAlerter funcDelegate={handleOutsideAlerter}>
      <div className={styles.filterChipDate}>
        <Chip
          data-testid={`filterChip${placeHolder}`}
          label={placeHolder}
          variant="outlined"
          clickable
          deleteIcon={<ArrowDropDownIcon />}
          onDelete={handleOnChipClicked}
          onClick={handleOnChipClicked}
          className={`${isAnyDateFilterFilled() ? styles.active : ''}`}
        />
        <Paper elevation={1} className={`${styles.options} ${filterChipIsOpen ? '' : styles.hidden}`}>
          <div className={styles.dateTimePickerRow}>
            <DateTimePicker
              ampm={false}
              closeOnSelect={false}
              label={t('from', 'From')}
              localeText={dateTimePickerLocaleText}
              value={dateFilters.dateFrom}
              onChange={handleDateFromChange}
              onOpen={openDatePicker}
              onClose={closeDatePicker}
              maxDateTime={dateFilters.dateTo ?? undefined}
              minDate={minimumStartDate}
              sx={{ mb: 2 }}
              onError={(error) => setHasError(!!error)}
              views={['hours', 'day']}
              viewRenderers={{
                hours: renderDigitalClockTimeView,
              }}
              format="DD-MM-YYYY HH:mm"
              slotProps={{
                toolbar: {
                  toolbarFormat: 'dddd DD MMMM',
                  hidden: false,
                  sx: customToolbarStyle,
                },
                actionBar: {
                  actions: ['cancel', 'clear', 'accept'],
                },
                popper: {
                  disablePortal: true,
                },
                day: selectedDayStyle,
              }}
            />
            <DateTimePicker
              ampm={false}
              closeOnSelect={false}
              label={t('to', 'To')}
              localeText={dateTimePickerLocaleText}
              value={dateFilters.dateTo}
              onChange={updateDateTo}
              onOpen={openDatePicker}
              onClose={closeDatePicker}
              disabled={!dateFilters.dateFrom}
              minDateTime={dateFilters.dateFrom ?? undefined}
              maxDateTime={maxDateForTheUntilFilter()}
              onError={(error) => setHasError(!!error)}
              views={['hours', 'day']}
              viewRenderers={{
                hours: renderDigitalClockTimeView,
              }}
              format="DD-MM-YYYY HH:mm"
              slotProps={{
                toolbar: {
                  toolbarFormat: 'dddd DD MMMM',
                  hidden: false,
                  sx: customToolbarStyle,
                },
                actionBar: {
                  actions: ['cancel', 'clear', 'accept'],
                },
                popper: {
                  disablePortal: true,
                },
                day: selectedDayStyle,
              }}
            />
          </div>
          <div className={styles.buttonsRow}>
            <Button variant="outlined" onClick={handleCancel}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button variant="contained" onClick={handleApply} disabled={hasError}>
              {t('apply', 'Apply')}
            </Button>
          </div>
        </Paper>
      </div>
    </OutsideAlerter>
  );
};

export default DateTimeRangeFilterChip;
